import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wifi, 
  Book, 
  MapPin, 
  Share2, 
  Calendar,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { trackEvent } from '../../utils/analytics';
import { useHubProperty, hubPath, slugForId, HUB_PROPERTIES } from './hubConfig';
import { HubLogo } from './HubBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Guest Hub Home Page
 * 
 * Central landing page for guests with big buttons linking to:
 * - Wi-Fi
 * - Guidebook
 * - Local Guide
 * - Social
 * - Book Direct
 * 
 * Routes:
 * - /hub - Default hub (select property)
 * - /hub/:propertyId - Property-specific hub
 */
const GuestHub = () => {
  const { slug, propertyId, hubProperty } = useHubProperty();
  const shortName = (p) => HUB_PROPERTIES.find((h) => h.id === p.id)?.name || p.name;
  const [property, setProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        trackEvent('guest_hub_view', { property_id: propertyId || 'home' });
        
        const response = await fetch(`${API_URL}/api/public/properties`);
        if (response.ok) {
          const data = await response.json();
          setProperties(data);
          
          if (propertyId) {
            const prop = data.find(p => p.id === propertyId);
            setProperty(prop);
          }
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [propertyId]);

  const hubLinks = [
    {
      id: 'wifi',
      title: 'Wi-Fi',
      description: 'Get connected instantly',
      icon: Wifi,
      color: 'bg-blue-500',
      path: slug ? hubPath(slug, 'wifi') : null
    },
    {
      id: 'guidebook',
      title: 'House Guide',
      description: 'Check-in, rules & how-tos',
      icon: Book,
      color: 'bg-amber-500',
      path: slug ? hubPath(slug, 'guidebook') : null
    },
    {
      id: 'local-guide',
      title: 'Local Guide',
      description: 'Restaurants, coffee & attractions',
      icon: MapPin,
      color: 'bg-green-500',
      path: slug ? hubPath(slug, 'local-guide') : null
    },
    {
      id: 'social',
      title: 'Follow Us',
      description: 'Tag us & get featured',
      icon: Share2,
      color: 'bg-pink-500',
      path: slug ? hubPath(slug, 'social') : null
    },
    {
      id: 'book',
      title: 'Book Direct',
      description: 'Save 35% on your next stay',
      icon: Calendar,
      color: 'bg-slate-800',
      path: slug ? hubPath(slug, 'book') : null
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div data-testid="guest-hub-page" className="min-h-screen bg-slate-900 max-w-lg mx-auto">
      {/* Header */}
      <div className="px-4 pt-8 pb-6 text-center">
        <div className="mb-5"><HubLogo /></div>
        
        <h1 className="text-2xl font-bold text-white mb-1">
          {property ? `Welcome to ${hubProperty?.name || property.name}` : 'Guest Hub'}
        </h1>
        <p className="text-slate-400 text-sm">
          {property ? 'Everything you need for your stay' : 'Select your property to get started'}
        </p>
      </div>

      {/* Property Selector (if no property selected) */}
      {!propertyId && properties.length > 0 && (
        <div className="px-4 pb-6">
          <p className="text-slate-400 text-sm mb-3 text-center">Which property are you staying at?</p>
          <div className="space-y-2">
            {properties.map(prop => (
              <Link 
                key={prop.id} 
                to={hubPath(slugForId(prop.id))}
                data-testid={`hub-select-${slugForId(prop.id)}`}
                className="block"
              >
                <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-700/50 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {prop.images?.[0] && (
                        <img 
                          src={prop.images[0]} 
                          alt={prop.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <p className="text-white font-medium">{shortName(prop)}</p>
                        <p className="text-slate-400 text-xs">{prop.location}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-500" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Hub Links */}
      <div className="px-4 pb-8">
        <div className="space-y-3">
          {hubLinks.map(link => {
            const Icon = link.icon;
            const isDisabled = !link.path;
            
            const content = (
              <Card 
                className={`overflow-hidden transition-all ${
                  isDisabled 
                    ? 'bg-slate-800/30 border-slate-700/50 opacity-50' 
                    : 'bg-slate-800/50 border-slate-700 hover:bg-slate-700/50 hover:scale-[1.02]'
                }`}
              >
                <CardContent className="p-0">
                  <div className="flex items-center">
                    <div className={`${link.color} w-16 h-16 flex items-center justify-center flex-shrink-0`}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1 px-4 py-3">
                      <h3 className="text-white font-semibold text-lg">{link.title}</h3>
                      <p className="text-slate-400 text-sm">{link.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-500 mr-4" />
                  </div>
                </CardContent>
              </Card>
            );
            
            if (isDisabled) {
              return (
                <div key={link.id} className="cursor-not-allowed">
                  {content}
                </div>
              );
            }
            
            return (
              <Link 
                key={link.id} 
                to={link.path}
                data-testid={`hub-link-${link.id}`}
                onClick={() => trackEvent('guest_hub_click', { section: link.id, property_id: propertyId })}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 pb-8 text-center">
        <p className="text-slate-500 text-xs mb-2">
          Need help? Contact us anytime
        </p>
        <a href="tel:+15715075650" className="text-amber-400 font-medium">
          (571) 507-5650
        </a>
      </div>
    </div>
  );
};

export default GuestHub;
